













































import { uniqueId } from 'lodash';
import { GridLayout, GridItem } from 'vue-grid-layout';
import { GridItemType, DEFAULT_LAYOUT_SETTINGS, LayoutSettings } from './model';
import AsyncComponentFactory from './items/AsyncComponent';

export default {
  name: 'Layout',
  components: {
    GridLayout,
    GridItem,
    DefaultItem: AsyncComponentFactory(import('./items/DefaultItem.vue')),
  },
  provide() {
    return {
      grid: this,
    };
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    settings: {
      type: Object as () => LayoutSettings,
      default() {
        return {};
      },
    },
  },
  computed: {
    margin(): Array<Number> {
      if (isValidMargin(this.settings.margin)) return this.settings.margin;
      return DEFAULT_LAYOUT_SETTINGS.margin;
    },
    columns(): Number {
      if (isPositiveInteger(this.settings.columns)) return this.settings.columns;
      return DEFAULT_LAYOUT_SETTINGS.columns;
    },
    itemsPerRow(): Number {
      if (isPositiveInteger(this.settings.itemsPerRow)) return this.settings.itemsPerRow;
      return DEFAULT_LAYOUT_SETTINGS.itemsPerRow;
    },
    rowHeight(): Number {
      if (isPositiveInteger(this.settings.rowHeight)) return this.settings.rowHeight;
      return DEFAULT_LAYOUT_SETTINGS.rowHeight;
    },
    showPosition(): Boolean {
      if (isBoolean(this.settings.showPosition)) return this.settings.showPosition;
      return DEFAULT_LAYOUT_SETTINGS.showPosition;
    },
    verticalCompact(): Boolean {
      if (isBoolean(this.settings.verticalCompact)) return this.settings.verticalCompact;
      return DEFAULT_LAYOUT_SETTINGS.verticalCompact;
    },
    useCssTransforms(): Boolean {
      if (isBoolean(this.settings.useCssTransforms)) return this.settings.useCssTransforms;
      return DEFAULT_LAYOUT_SETTINGS.useCssTransforms;
    },
    isResizable(): Boolean {
      if (isBoolean(this.settings.isResizable)) return this.settings.isResizable;
      return DEFAULT_LAYOUT_SETTINGS.isResizable;
    },
    isDraggable(): Boolean {
      if (isBoolean(this.settings.isDraggable)) return this.settings.isDraggable;
      return DEFAULT_LAYOUT_SETTINGS.isDraggable;
    },
  },
  watch: {
    items() {
      this.checkLayoutConsistency();
    },
  },
  created() {
    this.checkLayoutConsistency();
  },
  methods: {
    refreshLayout() {
      this.$refs.layout.layoutUpdate();
    },
    onLayoutUpdated(layout) {
      this.$emit('layout-changed', layout);
    },
    hasInconsistentLayout(item) {
      return (
        !isPositiveInteger(item.x, true) ||
        !isPositiveInteger(item.y, true) ||
        !isPositiveInteger(item.w) ||
        !isPositiveInteger(item.h) ||
        item.x >= this.columns ||
        item.w > this.columns ||
        item.x + item.w > this.columns
      );
    },
    checkLayoutConsistency() {
      // if the provided items does not have the proper fields, or they are not consistent
      // in relation with the amount of columns and the items widths,
      // it initializes the layout with default configurations
      const shouldRedefineLayout = this.items.some(item => this.hasInconsistentLayout(item));
      this.items.forEach((item: GridItemType, index) => {
        let i = item.i || item.id || uniqueId();
        item.i = i;
        if (shouldRedefineLayout) {
          this.setDefaultPosition(item, index);
        }
      });
    },
    setDefaultPosition(item, index) {
      const amountPerRow = this.itemsPerRow;
      item.w = this.columns / amountPerRow;
      item.h = 1;
      item.x = (index % amountPerRow) * item.w;
      item.y = Math.trunc(index / amountPerRow);
      item.minH = item.h;
    },
    restoreDefault() {
      this.items.forEach((item: GridItemType, index) => {
        this.setDefaultPosition(item, index);
      });
      this.refreshLayout();
    },
  },
};

function isPositiveInteger(value, equals0 = false) {
  const isInteger = Number.isInteger(value);
  return isInteger && (equals0 ? value >= 0 : value > 0);
}

function isBoolean(value) {
  return typeof value === 'boolean';
}

function isValidMargin(value) {
  return (
    Array.isArray(value) && value.length === 2 && isPositiveInteger(value[0], true) && isPositiveInteger(value[1], true)
  );
}
